import React, { useState, useEffect } from "react";
import { SearchIcon, Plus, RotateCw, X, FilterX } from "lucide-react";
import {
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import UserCard from "./Components/UserCard";
import CreateUserModal from "./Components/CreateUserModal";
import { Permissions } from "../../../permissions";
import { dummyUsers, dummyRoles } from "../../../dummyData";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";

const User = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: "",
    status: "",
  });

  // Check user permissions
  const canCreateUser = Permissions.includes("create_employee");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Wait 500ms after last keystroke

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load user data
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search query and filters
  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          user.role.toLowerCase().includes(query),
      );
    }

    // Apply role filter
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(user => user.status === filters.status);
    }

    setFilteredUsers(filtered);
  }, [debouncedSearch, users, filters]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      // In a real app, fetch from API
      // const response = await api.get('/users');
      // const data = response.data;

      // Using dummy data for demo
      await new Promise(resolve => setTimeout(resolve, 500));
      const data = dummyUsers;

      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refreshing the data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchUsers();
      // In a real app, you might show a success toast
      // successToast("User list refreshed");
    } catch (err) {
      // In a real app, you might show an error toast
      // errorToast("Failed to refresh user list");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle search input change
  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  // Clear search input
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      role: "",
      status: "",
    });
  };

  // Handle user creation from modal
  const handleCreateUser = userData => {
    // In a real app, you would call an API
    // For now, just add to the local state with a new ID
    const newUser = {
      id: users.length + 1,
      ...userData,
      status: "active",
      lastLogin: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    setUsers([...users, newUser]);
  };

  // Handle user deletion
  const handleDeleteUser = async userId => {
    try {
      // In a real app, call API to delete
      // await api.delete(`/users/${userId}`);

      // Mock deletion
      await new Promise(resolve => setTimeout(resolve, 800));

      // Update local state
      const updatedUsers = users.filter(user => user.id !== userId);
      setUsers(updatedUsers);
    } catch (err) {
      console.error("Error deleting user:", err);
      // In a real app, show an error toast notification
    }
  };

  // Handle user edit
  const handleEditUser = (userId, updatedData) => {
    // In a real app, call API to update the user
    // For now, update the local state
    const updatedUsers = users.map(user =>
      user.id === userId ? { ...user, ...updatedData } : user,
    );
    setUsers(updatedUsers);
  };

  // Get unique roles for filter
  const availableRoles = [...new Set(users.map(user => user.role))];

  return (
    <div>
      <div className="bg-white h-[calc(100vh-120px)]">
        {/* Header Section */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Users
          </h1>

          {/* Search and Action Buttons */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="w-[550px]">
              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={18}
                  color="#83899F"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, role..."
                  className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                           focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                           outline-none transition-all text-gray-600"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                    <X size={16} color="#83899F" />
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {canCreateUser && (
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all text-sm font-medium">
                  <Plus size={16} className="mr-2" />
                  <span className="font-medium">Create new User</span>
                </button>
              )}
              <IconButton
                onClick={handleRefresh}
                className="hover:bg-gray-100"
                disabled={isRefreshing}
                title="Refresh">
                <RotateCw
                  size={20}
                  className={`text-gray-600 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
              </IconButton>
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <Alert severity="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Filters */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Role Filter */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={e => handleFilterChange("role", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All Roles</MenuItem>
                  {availableRoles.map(role => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={e => handleFilterChange("status", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || filters.role || filters.status) && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                         hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
          </div>

          {/* User cards */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg">
              <CircularProgress size={40} sx={{ color: "#5652B0" }} />
              <p className="mt-4 text-gray-500 font-outfit">Loading users...</p>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredUsers.map(user => (
                <UserCard
                  key={user.id}
                  user={user}
                  onDelete={handleDeleteUser}
                  onEdit={handleEditUser}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 mb-2 font-outfit">No users found</p>
              {(searchQuery || filters.role || filters.status) && (
                <p className="text-sm text-gray-400 font-outfit">
                  Try adjusting your search filters
                </p>
              )}
              {canCreateUser &&
                !searchQuery &&
                !filters.role &&
                !filters.status && (
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 mt-4 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all">
                    <Plus size={16} className="mr-2" />
                    <span className="font-medium">Create your first user</span>
                  </button>
                )}
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        open={createModalOpen}
        handleClose={() => setCreateModalOpen(false)}
        onSave={handleCreateUser}
      />
    </div>
  );
};

export default User;

{
  /**
   * https://mui.com/material-ui/react-select/\
   *
   * answer to side frop bar check ot out
   * s
   *
   */
}
